import { useEffect, useRef, useState } from 'react'
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Collapse,
  useTheme,
  useMediaQuery,
  Button,
  alpha,
} from '@mui/material'
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Science as TestTubeIcon,
  Search as SearchIcon,
  ShoppingCart as OrdersIcon,
  ReportProblem as ComplaintsIcon,
  Flag as AbuseReportsIcon,
  Store as StoreIcon,
  Map as MapIcon,
  CardMembership as SubscriptionsIcon,
  Analytics as AnalyticsIcon,
  Loyalty as CouponsIcon,
  AccountBalanceWallet as WalletsIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  PhoneAndroid as AppSettingsIcon,
  Assessment as ReportsIcon,
  NotificationsNone as NotificationsIcon,
  Sms as SmsIcon,
  VpnKey as VpnKeyIcon,
  MarkEmailRead as MessageLogIcon,
} from '@mui/icons-material'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth, db } from '../../firebase'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import Badge from '@mui/material/Badge'

const DRAWER_WIDTH = 280

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  showModerationBadge?: boolean
}

interface NavGroup {
  label: string
  icon: React.ReactNode
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    label: 'المستخدمون والمنتجات',
    icon: <PeopleIcon />,
    items: [
      { to: '/users', label: 'المستخدمون', icon: <PeopleIcon /> },
      { to: '/message-history', label: 'سجل الرسائل', icon: <MessageLogIcon /> },
      { to: '/products', label: 'المنتجات', icon: <InventoryIcon /> },
      { to: '/test-products', label: 'منتجات اختبار', icon: <TestTubeIcon /> },
      { to: '/search', label: 'بحث المنتجات', icon: <SearchIcon /> },
      { to: '/otp-monitor', label: 'OTP Monitor', icon: <SmsIcon /> },
      { to: '/auth-phone-monitor', label: 'مراقبة الهاتف/كلمة المرور', icon: <VpnKeyIcon /> },
    ],
  },
  {
    label: 'الطلبات والشكاوى',
    icon: <OrdersIcon />,
    items: [
      { to: '/orders', label: 'الطلبات', icon: <OrdersIcon /> },
      { to: '/complaints', label: 'الشكاوى', icon: <ComplaintsIcon /> },
      {
        to: '/abuse-reports',
        label: 'البلاغات والمحتوى المخالف',
        icon: <AbuseReportsIcon />,
        showModerationBadge: true,
      },
    ],
  },
  {
    label: 'التجار والمتاجر',
    icon: <StoreIcon />,
    items: [
      { to: '/store-activation', label: 'تفعيل المتاجر', icon: <StoreIcon /> },
      { to: '/merchants-map', label: 'خريطة التجار', icon: <MapIcon /> },
      { to: '/merchant-subscriptions', label: 'اشتراكات التجار', icon: <SubscriptionsIcon /> },
      { to: '/seller-analytics', label: 'تحليل التجار', icon: <AnalyticsIcon /> },
    ],
  },
  {
    label: 'المالية والدفع',
    icon: <PaymentIcon />,
    items: [
      { to: '/coupons', label: 'الكوبونات', icon: <CouponsIcon /> },
      { to: '/wallets', label: 'المحافظ', icon: <WalletsIcon /> },
      { to: '/payments', label: 'إعدادات الدفع', icon: <PaymentIcon /> },
      { to: '/financial-transactions', label: 'المعاملات المالية', icon: <ReceiptIcon /> },
    ],
  },
  {
    label: 'الإعدادات والتقارير',
    icon: <SettingsIcon />,
    items: [
      { to: '/settings', label: 'الإعدادات', icon: <SettingsIcon /> },
      { to: '/auth-methods', label: 'إدارة طرق الدخول', icon: <SettingsIcon /> },
      { to: '/app-settings', label: 'إعدادات التطبيق', icon: <AppSettingsIcon /> },
      { to: '/reports', label: 'التقارير', icon: <ReportsIcon /> },
    ],
  },
]

/** عدد البلاغات المعلّقة من بلاغات المستخدمين + نتائج الفحص التلقائي للصور (بدون تكرار) */
function useModerationPendingCount(): number {
  const [reportsCount, setReportsCount] = useState(0)
  const [flagsCount, setFlagsCount] = useState(0)

  useEffect(() => {
    const unsubReports = onSnapshot(
      query(collection(db, 'request_reports'), where('status', '==', 'pending')),
      (snap) => setReportsCount(snap.size),
      () => setReportsCount(0)
    )
    const unsubFlags = onSnapshot(
      query(collection(db, 'moderation_flags'), where('status', '==', 'pending')),
      (snap) =>
        setFlagsCount(
          snap.docs.filter((d) => (d.data() as { source?: string })?.source !== 'user_report').length
        ),
      () => setFlagsCount(0)
    )
    return () => {
      unsubReports()
      unsubFlags()
    }
  }, [])

  return reportsCount + flagsCount
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()
  const theme = useTheme()
  const moderationPendingCount = useModerationPendingCount()
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>(() => {
    const path = location.pathname
    const initial: Record<number, boolean> = {}
    navGroups.forEach((g, gi) => {
      const hasActive = g.items.some((i) => i.to === path || path.startsWith(i.to + '/'))
      initial[gi] = hasActive
    })
    return initial
  })

  const toggleGroup = (index: number) => {
    setOpenGroups((prev) => ({ ...prev, [index]: !prev[index] }))
  }

  return (
    <Box sx={{ py: 2, px: 1.5 }}>
      <ListItemButton
        component={Link}
        to="/users"
        onClick={onNavigate}
        sx={{
          px: 2,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DashboardIcon sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
          كارسال
        </Typography>
      </ListItemButton>
      <List disablePadding>
        {navGroups.map((group, groupIndex) => {
          const isOpen = openGroups[groupIndex] ?? true
          return (
            <Box key={groupIndex}>
              <ListItemButton
                onClick={() => toggleGroup(groupIndex)}
                sx={{
                  mx: 1,
                  mt: 1,
                  borderRadius: 2,
                  py: 1.25,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.text.secondary }}>
                  {group.icon}
                </ListItemIcon>
                <ListItemText
                  primary={group.label}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                />
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {group.items.map((item) => {
                    const isSelected =
                      location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                    return (
                      <ListItemButton
                        key={item.to}
                        component={Link}
                        to={item.to}
                        selected={isSelected}
                        onClick={onNavigate}
                        sx={{
                          pl: 4,
                          py: 0.9,
                          mx: 1,
                          borderRadius: 2,
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                        {item.showModerationBadge && moderationPendingCount > 0 ? (
                          <Badge
                            badgeContent={
                              moderationPendingCount > 99 ? '99+' : moderationPendingCount
                            }
                            color="error"
                            sx={{ mr: 1 }}
                          />
                        ) : null}
                      </ListItemButton>
                    )
                  })}
                </List>
              </Collapse>
            </Box>
          )
        })}
      </List>
    </Box>
  )
}

const SIDEBAR_COLLAPSED_WIDTH = '2cm' // جزء ظاهر من القائمة عند الإغلاق

export default function AdminLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopSidebarHover, setDesktopSidebarHover] = useState(false)
  const navigate = useNavigate()
  const [pendingActivationsCount, setPendingActivationsCount] = useState<number>(0)
  const previousPendingCountRef = useRef<number | null>(null)

  // الاستماع لطلبات تفعيل المتجر المعلقة لإظهار تنبيه في الهيدر + تشغيل صوت عند كل زيادة
  useEffect(() => {
    const q = query(collection(db, 'store_activation_requests'), where('status', '==', 'pending'))
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const newCount = snapshot.size
        const prev = previousPendingCountRef.current
        if (prev !== null && newCount > prev) {
          try {
            const audio = new Audio('/sounds/s3andyyy.wav')
            audio.play().catch(() => {})
          } catch (_) {}
        }
        previousPendingCountRef.current = newCount
        setPendingActivationsCount(newCount)
      },
      (error) => {
        console.warn('Store activation notifications listener error:', error)
        setPendingActivationsCount(0)
      }
    )
    return () => unsub()
  }, [])

  const logout = async () => {
    await signOut(auth)
    navigate('/login')
  }

  const quickLinks = [
    { label: 'المستخدمون', path: '/users' },
    { label: 'OTP Monitor', path: '/otp-monitor' },
    { label: 'مراقبة الهاتف', path: '/auth-phone-monitor' },
    { label: 'البلاغات والمحتوى المخالف', path: '/abuse-reports' },
    { label: 'خريطة التجار', path: '/merchants-map' },
    { label: 'الطلبات', path: '/orders' },
  ]

  const sidebarBox = (
    <Box
      component="nav"
      onMouseEnter={() => setDesktopSidebarHover(true)}
      onMouseLeave={() => setDesktopSidebarHover(false)}
      sx={{
        width: desktopSidebarHover ? DRAWER_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        overflow: 'hidden',
        borderLeft: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        transition: theme.transitions.create('width', { duration: 200, easing: 'easeInOut' }),
      }}
    >
      <Box sx={{ display: 'flex', width: DRAWER_WIDTH, height: '100%', flexDirection: 'row-reverse' }}>
        <Box sx={{ width: SIDEBAR_COLLAPSED_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MenuIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
          <SidebarContent />
        </Box>
      </Box>
    </Box>
  )

  const mobileDrawer = (
    <Drawer
      variant="temporary"
      anchor="right"
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: 'none',
          borderLeft: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        },
      }}
    >
      <SidebarContent onNavigate={() => setMobileOpen(false)} />
    </Drawer>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* القائمة على اليمين: في RTL العنصر الأول يظهر على اليمين */}
      {!isMobile && sidebarBox}
      {isMobile && mobileDrawer}
      {/* المحتوى الرئيسي يأخذ الباقي ولا يُغطى */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 3 } }}>
            {/* زر القائمة للموبايل */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flex: 1 }}>
              <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Box>

            {/* اختصارات سريعة لأكثر الصفحات استخداماً */}
            <Box
              sx={{
                flex: 1,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {quickLinks.map((link) => (
                <Button
                  key={link.path}
                  size="medium"
                  variant="outlined"
                  onClick={() => navigate(link.path)}
                  sx={{
                    borderRadius: 1,
                    px: 2.25,
                    minWidth: 118,
                    height: 40,
                    maxHeight: 40,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>

            {/* أيقونة تنبيه تفعيل المتاجر + زر تسجيل الخروج */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                color={pendingActivationsCount > 0 ? 'warning' : 'default'}
                onClick={() => navigate('/store-activation')}
                sx={{
                  bgcolor:
                    pendingActivationsCount > 0
                      ? alpha(theme.palette.warning.main, 0.08)
                      : 'transparent',
                  '&:hover': {
                    bgcolor:
                      pendingActivationsCount > 0
                        ? alpha(theme.palette.warning.main, 0.16)
                        : alpha(theme.palette.action.hover, 0.08),
                  },
                }}
              >
                <Badge
                  color="error"
                  badgeContent={pendingActivationsCount > 99 ? '99+' : pendingActivationsCount}
                  invisible={pendingActivationsCount === 0}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              <Button
                color="inherit"
                onClick={logout}
                startIcon={<LogoutIcon />}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main },
                }}
              >
                تسجيل الخروج
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, md: 3 },
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
